import { useCallback, useEffect, useRef } from 'preact/hooks';

const POPUP_TARGET = 'zl-speedy-auth';
const SUCCESS_MESSAGE = 'zl-sso-success';
const FAILURE_MESSAGE = 'zl-sso-failure';

interface UseSsoPopupOptions {
  onSuccess: () => void;
  onFailure: () => void;
}

/**
 * Runs a social login in a new tab, so the page behind the modal is never torn down.
 *
 * zl-app and the API share an origin, so the callback page is same-origin with its opener and can
 * simply post a message and close itself — no backend change. When the browser blocks the popup
 * there is no way to keep the page alive, so the caller falls back to a same-tab redirect carrying
 * the serialised flow state, the way `ppt` and the checkout redirect params already do.
 */
const useSsoPopup = ({ onSuccess, onFailure }: UseSsoPopupOptions) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cleanupRef.current?.();
    },
    [],
  );

  const open = useCallback(
    (url: string) => {
      // Whatever the last call left running is finished with. `window.open` reuses the named
      // target, so a second social button hands this invocation the *same* tab the first one is
      // watching — two watchers on one popup, and only the newer reachable through the ref.
      cleanupRef.current?.();

      const popup = window.open(url, POPUP_TARGET);
      if (!popup) {
        return false;
      }

      /**
       * This invocation's own teardown, closed over rather than reached through the ref.
       *
       * Every closure below used to go via `cleanupRef.current`, which is only ever the *latest*
       * one. Opening a second popup orphaned the first: its interval could no longer clear itself,
       * so once the newer cleanup had nulled the ref it went on calling `onFailure` every 500 ms
       * for the life of the page, dispatching `ssoFailed` over and over.
       */
      let cleanup = () => {};

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data?.type === SUCCESS_MESSAGE) {
          cleanup();
          onSuccess();
        }
        if (event.data?.type === FAILURE_MESSAGE) {
          cleanup();
          onFailure();
        }
      };

      // A tab closed by hand says nothing. Treat that as a failure rather than hanging: unlike
      // checkout, nothing was charged, so there is no reason to go looking for a side effect.
      const closedWatcher = setInterval(() => {
        if (popup.closed) {
          cleanup();
          onFailure();
        }
      }, 500);

      cleanup = () => {
        clearInterval(closedWatcher);
        window.removeEventListener('message', onMessage);
        // Only stand down as the current cleanup if this invocation still is it.
        if (cleanupRef.current === cleanup) {
          cleanupRef.current = null;
        }
      };
      cleanupRef.current = cleanup;

      window.addEventListener('message', onMessage);
      return true;
    },
    [onFailure, onSuccess],
  );

  return { open };
};

export default useSsoPopup;
